import React from 'react';
import { Copy, Check, Crown, UserPlus, ChevronRight, Shield, Ban } from 'lucide-react';
import ShareLeague from './ShareLeague';

/**
 * A league, as the heading over its own games.
 *
 * Replaces the card that used to sit on a separate My Leagues tab. It kept the same
 * badges on purpose: a vacancy is flagged here as well as on the league page, because
 * games carry on regenerating without a manager and nothing otherwise forces a member
 * to open the league and find out.
 *
 * The whole header opens the league, the way the whole of a game row opens the game.
 * It used to carry a "Table" link instead, which was both a second target competing
 * with the card and a label describing one tab of where it went - the league page is
 * also the members, the join requests, the Gaffer, and forking the same crew into a
 * new league. The chevron is left as the affordance because a heading does not
 * otherwise read as something you can click; the button underneath it is the real
 * control, so the name is still what a screen reader announces.
 *
 * No description of what a Mini League is. By the time somebody is looking at this,
 * they are in one - the explaining is done at the point of choosing.
 */
function LeagueGroupHeader({ league, currentUser, copiedCode, onCopyCode, onViewLeague }) {
  const isManager = !league.vacant && String(league.managerId) === String(currentUser.id);
  const ended = league.cancelled || league.complete;

  return (
    <div className={`group-header league-group-header${league.cancelled ? ' is-cancelled' : ''}`}>
      <div className="group-header-top">
        <Shield size={18} className="group-header-icon" />
        {/* The button sits inside the heading rather than round it: a heading is not
            phrasing content and cannot legally live inside a button. */}
        <h2 className="group-header-name">
          <button className="group-header-open" onClick={() => onViewLeague(league.id)}>
            {league.name}
          </button>
        </h2>
        {/* A cancelled league outranks both of the others. Badging somebody Manager of a
            thing that no longer runs, or asking for a volunteer to take it over, are both
            answers to a question nobody is still asking. */}
        {league.cancelled ? (
          <span className="cancelled-badge">
            <Ban size={14} />
            Cancelled
          </span>
        ) : (
          <>
            {isManager && (
              <span className="manager-badge">
                <Crown size={14} />
                Manager
              </span>
            )}
            {league.vacant && (
              <span className="vacancy-badge">
                <UserPlus size={14} />
                Needs a manager
              </span>
            )}
          </>
        )}
        <ChevronRight size={18} className="group-header-chevron" aria-hidden="true" />
      </div>

      {/*
        A cancelled league did not finish, so it says something different: there is no winner
        to go and look at, there is a reason. Whoever called it off is named because "your
        manager ended this" and "we ended this" are not the same message. The same words as
        the league page uses, shortened - this is a heading, not the announcement.
      */}
      {league.cancelled && (
        <div className="group-header-note">
          {league.cancelledBy === 'ADMIN' ? 'Cancelled by an administrator' : 'Cancelled by the manager'}
          {league.cancellationReason ? ` — “${league.cancellationReason}”` : ''}
        </div>
      )}

      {league.complete && (
        <div className="league-winner">
          {league.winners && league.winners.length > 0 ? (
            <>&#127942; <strong>{league.winners.join(' & ')}</strong>{' '}
              {league.winners.length > 1 ? 'share the league' : 'wins the league'}
            </>
          ) : (
            <>League finished &mdash; no winner</>
          )}
        </div>
      )}

      {/* Only while there is something to join. A code on a league that has ended is an
          invitation to a thing that is not happening. */}
      {league.leagueCode && !ended && (
        <div className="group-header-code">
          <span className="code-value">{league.leagueCode}</span>
          <button className="copy-btn" onClick={() => onCopyCode(league.leagueCode)} title="Copy league code">
            {copiedCode === league.leagueCode ? <Check size={16} /> : <Copy size={16} />}
          </button>
          <ShareLeague league={league} />
        </div>
      )}
    </div>
  );
}

export default LeagueGroupHeader;
